import React, { useState } from 'react';
import { BannerElement } from '../types';
import { Eye, EyeOff, Type, Image, Square, MousePointer, Minus, Edit2, Check, X } from 'lucide-react';

interface LayerPanelProps {
  elements: BannerElement[];
  selectedElement: string | null;
  onSelectElement: (id: string) => void;
  onMoveLayer: (id: string, direction: 'up' | 'down') => void;
  onToggleVisibility: (id: string) => void;
  onUpdateElement: (id: string, updates: Partial<BannerElement>) => void;
}

export const LayerPanel: React.FC<LayerPanelProps> = ({
  elements,
  selectedElement,
  onSelectElement,
  onMoveLayer,
  onToggleVisibility,
  onUpdateElement
}) => {
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [draggedElement, setDraggedElement] = useState<string | null>(null);
  const [dragOverElement, setDragOverElement] = useState<string | null>(null);

  const getElementIcon = (type: BannerElement['type']) => {
    switch (type) {
      case 'text': return <Type size={16} />;
      case 'image': return <Image size={16} />;
      case 'shape': return <Square size={16} />;
      case 'button': return <MousePointer size={16} />;
      case 'divider': return <Minus size={16} />;
      default: return <Square size={16} />;
    }
  };

  const getElementName = (element: BannerElement) => {
    if (element.customName) return element.customName;
    
    switch (element.type) {
      case 'text':
      case 'button':
        return element.content || `${element.type.charAt(0).toUpperCase() + element.type.slice(1)}`;
      case 'image':
        return element.dataBinding ? `Image (${element.dataBinding})` : 'Image';
      default:
        return element.type.charAt(0).toUpperCase() + element.type.slice(1);
    }
  };

  const handleStartEdit = (element: BannerElement) => {
    setEditingName(element.id);
    setEditingValue(getElementName(element));
  };

  const handleSaveEdit = (elementId: string) => {
    onUpdateElement(elementId, { customName: editingValue });
    setEditingName(null);
    setEditingValue('');
  };

  const handleCancelEdit = () => {
    setEditingName(null);
    setEditingValue('');
  };

  const handleDragStart = (e: React.DragEvent, elementId: string) => {
    setDraggedElement(elementId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, elementId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverElement(elementId);
  };

  const handleDragLeave = () => {
    setDragOverElement(null);
  };

  const handleDrop = (e: React.DragEvent, targetElementId: string) => {
    e.preventDefault();
    
    if (!draggedElement || draggedElement === targetElementId) {
      setDraggedElement(null);
      setDragOverElement(null);
      return;
    }

    const draggedIndex = elements.findIndex(el => el.id === draggedElement);
    const targetIndex = elements.findIndex(el => el.id === targetElementId);
    
    if (draggedIndex === -1 || targetIndex === -1) return;

    // Determine if we should move up or down based on drag position
    const direction = draggedIndex > targetIndex ? 'up' : 'down';
    
    // Calculate how many positions to move
    const positions = Math.abs(draggedIndex - targetIndex);
    
    // Move the element step by step
    for (let i = 0; i < positions; i++) {
      onMoveLayer(draggedElement, direction);
    }

    setDraggedElement(null);
    setDragOverElement(null);
  };

  // Sort elements by z-index (highest first for display)
  const sortedElements = [...elements].sort((a, b) => b.zIndex - a.zIndex);

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-3">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Layers</h3>
        
        <div className="space-y-1">
          {sortedElements.map((element, index) => (
            <div
              key={element.id}
              draggable
              onDragStart={(e) => handleDragStart(e, element.id)}
              onDragOver={(e) => handleDragOver(e, element.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, element.id)}
              className={`group flex items-center space-x-2 p-2 rounded cursor-pointer transition-all ${
                selectedElement === element.id 
                  ? 'bg-blue-100 border border-blue-300' 
                  : 'hover:bg-gray-50 border border-transparent'
              } ${
                dragOverElement === element.id ? 'border-blue-500 bg-blue-50' : ''
              } ${
                draggedElement === element.id ? 'opacity-50' : ''
              }`}
              onClick={() => onSelectElement(element.id)}
            >
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <div className="text-gray-500 cursor-grab">
                  {getElementIcon(element.type)}
                </div>
                
                {editingName === element.id ? (
                  <div className="flex items-center space-x-1 flex-1">
                    <input
                      type="text"
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      className="flex-1 px-1 py-0 text-sm border border-blue-300 rounded focus:outline-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveEdit(element.id);
                        if (e.key === 'Escape') handleCancelEdit();
                      }}
                      autoFocus
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSaveEdit(element.id);
                      }}
                      className="p-1 hover:bg-green-100 rounded"
                    >
                      <Check size={10} className="text-green-600" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelEdit();
                      }}
                      className="p-1 hover:bg-red-100 rounded"
                    >
                      <X size={10} className="text-red-600" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1 flex-1 min-w-0">
                    <span className="text-sm text-gray-700 truncate">
                      {getElementName(element)}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartEdit(element);
                      }}
                      className="p-1 hover:bg-gray-200 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Edit2 size={10} />
                    </button>
                  </div>
                )}
              </div>
              
              <div className="flex items-center space-x-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleVisibility(element.id);
                  }}
                  className="p-1 hover:bg-gray-200 rounded"
                  title="Toggle Visibility"
                >
                  {element.styles.opacity === 0 ? <EyeOff size={12} /> : <Eye size={12} />}
                </button>
              </div>
            </div>
          ))}
          
          {elements.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              <div className="text-sm">No layers yet</div>
              <div className="text-xs text-gray-400 mt-1">Add elements to see them here</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};